import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Navigation, AuthCallback, AuthLogout, ProtectedRoute, VersionDisplay } from './components';
import {
  AdminAuditLogPage,
  AdminInvitationsPage,
  AdminRolesPage,
  AdminSystemSettingsPage,
  AdminUsersPage,
  DashboardPage,
  EventDetailPage,
  EventEditPage,
  EventOrganizersPage,
  EventsPage,
  EventTransferPage,
  HomePage,
  InvitationsPage,
  LoginPage,
  ProfileCompletionPage,
  ProfilePage,
  PublicEventsPage,
  SessionApprovalPage,
  SessionDetailPage,
  SessionFormPage,
  SessionInvitationAcceptPage,
  SessionInvitationsPage,
  SessionsPage,
  SignupPage,
  SpeakerSearchPage,
  TalkDetailPage,
  TalkFormPage,
  TalksPage,
  UnauthorizedPage,
  UserProfilePage,
  UserSearchPage
} from './pages';
import './App.css';

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <Navigation />

          <main className="min-vh-100">
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/logout" element={<AuthLogout />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin routes */}
              <Route
                path="/admin/roles"
                element={
                  <ProtectedRoute requireRole="admin">
                    <AdminRolesPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/invitations"
                element={
                  <ProtectedRoute requireRole="admin">
                    <AdminInvitationsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute requireRole="admin">
                    <AdminUsersPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute requireRole="admin">
                    <AdminSystemSettingsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/audit-log"
                element={
                  <ProtectedRoute requireRole="admin">
                    <AdminAuditLogPage />
                  </ProtectedRoute>
                }
              />

              {/* Talk routes */}
              <Route
                path="/talks"
                element={
                  <ProtectedRoute>
                    <TalksPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/talks/new"
                element={
                  <ProtectedRoute>
                    <TalkFormPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/talks/:talkId"
                element={
                  <ProtectedRoute>
                    <TalkDetailPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/talks/:talkId/edit"
                element={
                  <ProtectedRoute>
                    <TalkFormPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/events"
                element={
                  <ProtectedRoute>
                    <EventsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/events/public"
                element={
                  <ProtectedRoute>
                    <PublicEventsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/events/:eventId"
                element={
                  <ProtectedRoute>
                    <EventDetailPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/events/:eventId/edit"
                element={
                  <ProtectedRoute>
                    <EventEditPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/events/:eventId/organizers"
                element={
                  <ProtectedRoute>
                    <EventOrganizersPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/invitations"
                element={
                  <ProtectedRoute>
                    <InvitationsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/events/:eventId/transfer"
                element={
                  <ProtectedRoute requireRole="admin">
                    <EventTransferPage />
                  </ProtectedRoute>
                }
              />

              {/* Session routes */}
              <Route
                path="/sessions"
                element={
                  <ProtectedRoute>
                    <SessionsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/sessions/new"
                element={
                  <ProtectedRoute>
                    <SessionFormPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/sessions/:sessionId"
                element={
                  <ProtectedRoute>
                    <SessionDetailPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/events/:eventId/invitations/session"
                element={
                  <ProtectedRoute>
                    <SessionInvitationsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/events/:eventId/sessions/approval"
                element={
                  <ProtectedRoute>
                    <SessionApprovalPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/invitations/:invitationId/accept"
                element={
                  <ProtectedRoute>
                    <SessionInvitationAcceptPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/messages"
                element={
                  <ProtectedRoute>
                    <div className="container mt-4">
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle me-2"></i>
                        Messaging system will be implemented in future tasks.
                      </div>
                    </div>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile/complete"
                element={
                  <ProtectedRoute>
                    <ProfileCompletionPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users/search"
                element={
                  <ProtectedRoute>
                    <UserSearchPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/speakers"
                element={
                  <ProtectedRoute>
                    <SpeakerSearchPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users/:userId"
                element={
                  <ProtectedRoute>
                    <UserProfilePage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <div className="container mt-4">
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle me-2"></i>
                        Settings will be implemented in future tasks.
                      </div>
                    </div>
                  </ProtectedRoute>
                }
              />

              {/* Catch all route */}
              <Route
                path="*"
                element={
                  <div className="container mt-5">
                    <div className="row justify-content-center">
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-body text-center">
                            <i className="fas fa-exclamation-triangle text-warning fa-3x mb-3"></i>
                            <h3>Page Not Found</h3>
                            <p className="text-muted">
                              The page you're looking for doesn't exist.
                            </p>
                            <button
                              className="btn btn-primary"
                              onClick={() => window.history.back()}
                            >
                              <i className="fas fa-arrow-left me-1"></i>
                              Go Back
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                }
              />
            </Routes>
          </main>

          <footer className="bg-light mt-5 py-4">
            <div className="container">
              <div className="row">
                <div className="col-md-6">
                  <p className="text-muted mb-0">
                    &copy; 2024 RateMyTalk. Built with <i className="fas fa-heart text-danger"></i> for the conference community.
                  </p>
                </div>
                <div className="col-md-6 text-end">
                  <VersionDisplay />
                </div>
              </div>
            </div>
          </footer>
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
